// TitleBar — the craft.js content-block wrapper for the Title Bar (heading band)
// block. Mirrors the Carousel/Image/Container block contract exactly: a useNode()
// component that connect(drag)s its root, wraps the render in <BlockIndicator>
// (builder chrome), and exposes a static `.craft` with default props +
// related.settings + displayName. The SAME component renders in the builder AND
// the published Segment view (Segment view mounts craft with enabled=false); we
// read `enabled` only to show the empty-state placeholder while authoring.
//
// Deck (source of truth): concepts/builder-blocks/15-titlebar-block-editor.html
// (round-7 v3, supersedes 14/13). ORCHESTRATOR scope (smallest block): heading
// text + background (REUSED from the Container #1996 picker) + alignment, with
// typography from tokens only.
//
// REUSE, NOT DUPLICATION (everything-is-a-shared-component): the background is
// resolved by the Container block's own `resolveContainerBackground` (#1996),
// and the color/opacity/palette handling matches Container exactly
// (usePaletteColor + applyOpacity + sliderToOpacity). This thin wrapper is the
// craft/redux adapter; all band markup lives in the craft-free TitleBarView.

import { useEditor, useNode } from "@craftjs/core";
import React from "react";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { applyOpacity, sliderToOpacity } from "../../../../utils/helpers";
import { resolveContainerBackground } from "../Container/containerBackground";
import { TITLE_BAR_DECK15_ENABLED } from "../../../../constants/featureFlags";
import { DEFAULT_BAR_COLOR } from "./titleBarLogic";
import { alignToPosition } from "./titleBarDeck15";
import { cropToBackgroundStyle } from "./barCrop";
import TitleBarView from "./TitleBarView";
import TitleBarSettings from "./TitleBarSettings";

export const TitleBar = ({
  title,
  containerFill,
  background,
  opacity,
  align,
  size,
  fontFamily,
  height,
  textTone,
  // DECK-15 delta props. They are NOT in TitleBarDefaultProps on purpose: with
  // the flag OFF the block writes none of them, so a saved segment's JSON is
  // byte-identical to today. Each falls back to the shipped behavior.
  cornerRounding,
  barWidth,
  position,
  overlayScope,
  overlayAdjust,
  customHeightPx,
  customSizePx,
  barCrop,
  margin,
}) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { getPaletteColor } = usePaletteColor();

  // Background: the same resolver the Container block uses (#1996). A structured
  // fill (Image/GIF · Gradient) paints its CSS; otherwise the legacy solid-color
  // path — palette-resolved + opacity-applied exactly like Container.
  // Crop-to-bar only applies to an IMAGE fill (a color/gradient/pattern has no
  // source image to crop) and only when the flag is on.
  const cropStyle =
    TITLE_BAR_DECK15_ENABLED &&
    containerFill &&
    containerFill.type === "image"
      ? cropToBackgroundStyle(barCrop)
      : null;

  const resolved = resolveContainerBackground(containerFill);
  let backgroundCss;
  let solidForContrast;
  if (resolved.mode === "fill") {
    backgroundCss = resolved.css;
    solidForContrast = null; // image/gradient: cannot sample -> AUTO uses white+scrim
  } else {
    const base = getPaletteColor(background || DEFAULT_BAR_COLOR);
    backgroundCss = applyOpacity(base, sliderToOpacity(opacity ?? 100));
    solidForContrast = base;
  }

  const marginStyle = margin
    ? `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`
    : undefined;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div style={{ padding: marginStyle }}>
          <TitleBarView
            title={title}
            backgroundCss={backgroundCss}
            solidForContrast={solidForContrast}
            textTone={textTone}
            align={align}
            size={size}
            fontFamily={fontFamily}
            height={height}
            editing={enabled}
            deck15={TITLE_BAR_DECK15_ENABLED}
            cornerRounding={cornerRounding ?? "m"}
            barWidth={barWidth ?? "full"}
            // An already-authored bar keeps the alignment its author chose: the
            // 1-D Left/Center/Right value maps onto the middle row of the grid.
            position={position ?? alignToPosition(align)}
            overlayScope={overlayScope ?? "none"}
            overlayAdjust={overlayAdjust ?? 0}
            customHeightPx={customHeightPx}
            customSizePx={customSizePx}
            cropStyle={cropStyle}
          />
        </div>
      </BlockIndicator>
    </div>
  );
};

export const TitleBarDefaultProps = {
  title: "",
  containerFill: null, // null => legacy solid-color path (byte-safe default)
  background: DEFAULT_BAR_COLOR,
  opacity: 100,
  align: "center", // deck: a heading band centers by default
  size: "m", // deck: Text size M (16px)
  fontFamily: "DM Sans", // deck: Font type DM Sans default (shared 9-font list)
  height: "m", // deck: Height M (52px)
  textTone: "auto", // deck: tone Auto (luminance-based flip)
  // fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
  // TitleBar had no margin prop/control at all.
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
};

TitleBar.craft = {
  props: TitleBarDefaultProps,
  related: {
    settings: TitleBarSettings,
  },
  displayName: "TitleBarWidget",
};

export default TitleBar;
